import Text from "@src/lib/ui/Text";
import * as React from "react";

import { cn } from "@src/lib/utils/cn";
import { VariantProps, cva } from "class-variance-authority";

export const cardVariants = cva(
  "relative rounded-3xl bg-card text-card-foreground",
  {
    variants: {
      variant: {
        default: "border",
        elevated: "shadow-sm border border-gray-2",
        "dotted-grid": "shadow-sm border border-gray-2 bg-dotted-grid",
        soft: "rounded-lg border-0 bg-transparent shadow-[0_0_0_1px_rgba(10,10,10,0.06),0_4px_24px_0_rgba(10,10,10,0.04)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ variant, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 pt-6 px-6 pb-4", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ children, className, ...props }, ref) => (
  <Text variant="display-bold" ref={ref} className={className} {...props}>
    {children}
  </Text>
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children, className, ...props }, ref) => (
  <Text
    variant="compact-description"
    ref={ref}
    className={cn("mt-1 text-muted-foreground", className)}
    {...props}
  >
    {children}
  </Text>
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
